/**
 * Chat stream — async generator consuming the OneStone /api/chat/stream SSE endpoint.
 * Translates OneStone's SSE event format into typed StreamEvents for ChatPanel.
 *
 * OneStone SSE events handled here (the parser below is the full list):
 *   text            → {type:'text', text: string}
 *   tool_call_start → {type:'tool_call_start', id, name}
 *   tool_call_input → {type:'tool_call_input', id, partialJson}
 *   tool_result     → {type:'tool_result', id, result, isError}
 *   turn_complete   → usage, cost and context for the turn in one payload
 *   route / turn_meta / system / question / question_resolved / subagent
 *   done            → {}
 *   error           → {type:'error', error: string}
 */
import { apiFetch } from './api';

export type StreamEvent =
  | { kind: 'text'; content: string }
  | {
      kind: 'tool_call';
      toolId: string;
      name: string;
      input: string;
      startedAt: number;
    }
  | {
      /** Streamed tool input — accumulated args delivered to the pending call. */
      kind: 'tool_call_input';
      toolId: string;
      input: string;
    }
  | {
      kind: 'tool_result';
      toolId: string;
      output: string;
      durationMs: number;
      isError: boolean;
      isWarning?: boolean;
    }
  | {
      /** Consolidated end-of-turn event — replaces the former separate usage,
       *  cost, and context events. Single source for all turn-completion data. */
      kind: 'turn_complete';
      usage: { inputTokens: number; outputTokens: number; cacheReadTokens: number; cacheWriteTokens: number };
      cost: {
        inputCost: number;
        outputCost: number;
        cacheReadCost: number;
        cacheWriteCost: number;
        totalCost: number;
        model: string;
      };
      durationMs: number;
      /** Provider-measured prompt fill — the single ground truth for context meters. */
      lastPromptTokens?: number;
      messageCount: number;
    }
  | { kind: 'interrupted' }
  /** Connection dropped mid-turn; auto-reconnecting to the still-running turn. */
  | { kind: 'reconnecting'; attempt: number }
  /** Server keepalive (`: ping`) — surfaced so the consumer can reset its stall timer. */
  | { kind: 'heartbeat' }
  | { kind: 'done' }
  | { kind: 'error'; message: string }
  /** Backend routing decision — the truth of what provider/model/tier runs this turn. */
  | {
      kind: 'route';
      provider: string;
      model: string;
      useExtendedContext: boolean;
      reason: string;
      /** The effort this turn's model is sent; '' = the model's default. */
      effort: string;
    }
  // Context estimate is now part of the `turn_complete` event.
  /** Informational server note rendered as a centered system row (non-fatal). */
  | { kind: 'system'; content: string }
  /** Per-turn injection metadata — what was injected into the agent's context. */
  | {
      kind: 'turn_meta';
      executor: 'cli' | 'api';
      systemPrompt: {
        sections: Array<{ name: string; chars: number; tokens: number; cached: boolean }>;
        totalChars: number;
        totalTokens: number;
      };
      budget: {
        modelWindow: number;
        maxOutput: number;
        systemReserved: number;
        historyBudget: number;
      };
      context: {
        messageCount: number;
        hasSummary: boolean;
        nearlyFull: boolean;
      };
    }
  /** Mid-turn operator question (ask_operator tool) — renders the interactive card. */
  | {
      kind: 'question';
      id: string;
      question: string;
      header?: string;
      questionKind: 'yes_no' | 'choice' | 'multi' | 'text';
      options: Array<{ label: string; description?: string }>;
      timeoutMs: number;
      askedAt: string;
    }
  /** Question settled (answered, declined, timed out, or turn cancelled) — removes the card. */
  | { kind: 'question_resolved'; id: string }
  /** Sub-agent live progress — tool calls and text emitted during sub-agent execution. */
  | {
      kind: 'subagent';
      subType: 'tool_start' | 'tool_input' | 'tool_result' | 'text';
      name?: string;
      id?: string;
      partialJson?: string;
      result?: string;
      isError?: boolean;
      text?: string;
    };

export interface ChatStreamRequest {
  message: string;
  sessionId?: string;
  provider?: string;
  model?: string;
  /** Opt into the model's extended context window (e.g. 1M on Opus/Sonnet via API). */
  useExtendedContext?: boolean;
  /** Ids `POST /api/chat/upload` returned. */
  attachmentIds?: string[];
  signal?: AbortSignal;
}

/**
 * Async generator — yields typed StreamEvents from the OneStone SSE endpoint.
 * Tool calls emit immediately on start so the UI shows a spinner with the
 * tool name. Results arrive later and update the same tool call in-place.
 */
/** Mutable state carried across the initial stream + any reconnect attempts. */
interface StreamState {
  /** Highest SSE event id (seq) seen — reconnect resumes with `?since=lastSeq`. */
  lastSeq: number;
  /**
   * The turn's run id, adopted from the server's `turn` attach event. If a
   * reconnect lands on a DIFFERENT runId, this generator's turn was superseded — its
   * seq bookkeeping is meaningless against the new run, so the stream ends
   * instead of mixing stale `since` state with the new turn's chunks.
   */
  runId: string | null;
  /** Conversation id — learned from the route event so a new convo can reconnect. */
  sessionId: string;
  /** Set once a terminal (done/error) event arrives — stops the reconnect loop. */
  terminal: boolean;
}

// Reconnect budget is *consecutive* failures, not lifetime attempts — it resets
// on any progress or wake (see streamChat), so an arbitrary number of sleep/wake
// cycles never exhausts it; only a sustained outage does.
const MAX_RECONNECT_ATTEMPTS = 30;
const reconnectDelayMs = (attempt: number): number => Math.min(800 * attempt, 5_000);

// The server emits a `: ping` keepalive every 15s (see sseHeartbeat.ts). If even
// that goes quiet for this long, the socket is half-open — typically a phone that
// slept and woke onto a dead TCP connection. We stop waiting on the zombie read
// and let the caller re-attach with `?since=` rather than block until the OS TCP
// timeout (minutes). Must exceed the 15s ping cadence with margin.
const STREAM_IDLE_TIMEOUT_MS = 25_000;

/** Resolve after `ms`, or early if any provided signal aborts. */
function sleep(ms: number, ...signals: Array<AbortSignal | undefined>): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, ms);
    for (const signal of signals) {
      signal?.addEventListener(
        'abort',
        () => {
          clearTimeout(t);
          resolve();
        },
        { once: true },
      );
    }
  });
}

/** One reader.read() result, plus an `idle` flag set when the idle-watchdog fired instead. */
interface ReadOutcome {
  done: boolean;
  // Explicit `| undefined` so a native ReadableStreamReadResult (whose `value`
  // is absent on done) assigns cleanly under exactOptionalPropertyTypes.
  value?: Uint8Array | undefined;
  idle?: boolean;
}

/**
 * Read the next chunk, but give up after `idleMs` of total silence (no bytes —
 * not even a heartbeat). On idle, cancel the reader and resolve with `idle:true`
 * so the caller treats it as a drop and reconnects, instead of blocking forever
 * on a half-open socket. Rejects (AbortError) if `signal` aborts — a real
 * user-initiated stop, which the caller must not turn into a reconnect.
 */
function readWithIdleTimeout(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  idleMs: number,
  signal?: AbortSignal,
): Promise<ReadOutcome> {
  return new Promise<ReadOutcome>((resolve, reject) => {
    let settled = false;
    // Auto-removes our abort listener on any settle (the {signal} option), so we
    // never leak one listener per read() across a long turn's many reads.
    const listenerCtl = new AbortController();
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      listenerCtl.abort();
      void reader.cancel().catch(() => undefined);
      resolve({ done: false, idle: true });
    }, idleMs);
    const finishAbort = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      listenerCtl.abort();
      void reader.cancel().catch(() => undefined);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    if (signal?.aborted) {
      finishAbort();
      return;
    }
    signal?.addEventListener('abort', finishAbort, { signal: listenerCtl.signal });
    reader.read().then(
      (r) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        listenerCtl.abort();
        resolve(r);
      },
      (e) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        listenerCtl.abort();
        reject(e as Error);
      },
    );
  });
}

/**
 * Send a message and stream the response, **surviving dropped connections**.
 * The turn runs durably server-side; if the stream drops mid-turn we re-attach
 * to the same turn with `?since=<lastSeq>` (replaying only the missed tail) and
 * keep going. `interrupted` is yielded only if reconnection ultimately fails.
 */
export async function* streamChat(req: ChatStreamRequest): AsyncGenerator<StreamEvent, void, unknown> {
  const body: Record<string, unknown> = {
    message: req.message,
  };
  if (req.sessionId) body.sessionId = req.sessionId;
  if (req.provider) body.provider = req.provider;
  if (req.model) body.model = req.model;
  if (req.useExtendedContext === true) body.useExtendedContext = true;
  if (req.attachmentIds && req.attachmentIds.length > 0) body.attachmentIds = req.attachmentIds;

  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
  if (req.signal) init.signal = req.signal;

  const res = await apiFetch(`/api/chat/stream`, init);

  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    try {
      const err = (await res.json()) as { error?: string };
      if (err.error) message = err.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  // Tool-call buffer persists across reconnects (start before a drop, result after).
  const toolBuffers = new Map<string, { name: string; input: string; startedAt: number }>();
  const state: StreamState = { lastSeq: -1, runId: null, sessionId: req.sessionId ?? '', terminal: false };

  // Wake trigger — a backgrounded mobile tab freezes timers and silently drops
  // its socket; on return (tab visible / network back) we reset the reconnect
  // budget and cut any pending backoff short so re-attach is immediate, not
  // delayed by a stale exponential backoff.
  let wakeBump = 0;
  let wakeAbort: AbortController | null = null;
  const onWake = (): void => {
    wakeBump += 1;
    wakeAbort?.abort();
  };
  const onVisible = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') onWake();
  };
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
  if (typeof window !== 'undefined') window.addEventListener('online', onWake);

  try {
    // Initial stream from the POST response (the first viewer of the durable turn).
    yield* consumeSse(res, state, toolBuffers, req.signal);

    // Reconnect loop — the turn keeps running server-side, so re-attach until it
    // produces a terminal event (or we exhaust *consecutive* failures / the user
    // aborts). The attempt counter resets on any progress or wake, so sleep/wake
    // churn never exhausts the budget — only a sustained outage does.
    let attempt = 0;
    let seenWake = wakeBump;
    while (!state.terminal && !req.signal?.aborted && state.sessionId) {
      if (wakeBump !== seenWake) {
        seenWake = wakeBump;
        attempt = 0; // woke up — give recovery a fresh budget
      }
      attempt += 1;
      if (attempt > MAX_RECONNECT_ATTEMPTS) break;
      yield { kind: 'reconnecting', attempt };
      wakeAbort = new AbortController();
      await sleep(reconnectDelayMs(attempt), req.signal, wakeAbort.signal);
      wakeAbort = null;
      if (req.signal?.aborted) break;

      const seqBefore = state.lastSeq;
      let rres: Response;
      try {
        const rinit: RequestInit = req.signal ? { signal: req.signal } : {};
        rres = await apiFetch(`/api/chat/sessions/${state.sessionId}/turn/stream?since=${state.lastSeq}`, rinit);
      } catch {
        continue; // network still down — back off and retry
      }
      if (!rres.ok) {
        if (rres.status === 404) break; // turn gone (swept) — give up gracefully
        continue;
      }
      yield* consumeSse(rres, state, toolBuffers, req.signal);
      // Received new events → the connection is healthy again; reset the failure
      // budget so a subsequent drop gets a full set of retries of its own.
      if (state.lastSeq > seqBefore) attempt = 0;
    }

    if (!state.terminal && !req.signal?.aborted) {
      // Reconnection exhausted — the agent may still be finishing server-side; the
      // consumer reloads persisted messages to reconcile.
      yield { kind: 'interrupted' };
    }
  } finally {
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    if (typeof window !== 'undefined') window.removeEventListener('online', onWake);
  }
}

/**
 * Parse one SSE response body into StreamEvents, tracking seq (`id:` lines) and
 * the terminal state. Returns when the body ends (drop) or a terminal event
 * arrives — the caller decides whether to reconnect.
 */
async function* consumeSse(
  res: Response,
  state: StreamState,
  toolBuffers: Map<string, { name: string; input: string; startedAt: number }>,
  signal?: AbortSignal,
): AsyncGenerator<StreamEvent, void, unknown> {
  const reader = res.body?.getReader();
  if (!reader) return;

  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = '';
  let currentId = '';
  let dataLines: string[] = [];

  try {
    while (true) {
      let chunk: ReadOutcome;
      try {
        chunk = await readWithIdleTimeout(reader, STREAM_IDLE_TIMEOUT_MS, signal);
      } catch {
        // The read threw — a connection reset mid-stream (the classic phone
        // sleep/wake drop, which the engine surfaces as a raw "error in input
        // stream" TypeError) or a deliberate user abort. Either way, stop reading
        // WITHOUT propagating: the caller's reconnect loop, guarded by
        // `signal.aborted`, decides whether to re-attach. This is THE fix that
        // keeps the raw browser error off the screen and lets `?since=` recovery
        // take over.
        return;
      }
      if (chunk.idle) return; // no bytes (not even a ping) for too long → reconnect
      if (chunk.done) break;

      buffer += decoder.decode(chunk.value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (line.startsWith(':')) {
          // Server keepalive (`: ping`). Surface it so the consumer's stall timer
          // resets on a healthy-but-quiet connection (long model-think gaps).
          yield { kind: 'heartbeat' };
          continue;
        }

        if (line.trim() === '') {
          // Empty line = end of SSE event
          if (currentId) {
            const seq = parseInt(currentId, 10);
            if (Number.isFinite(seq) && seq > state.lastSeq) state.lastSeq = seq;
            currentId = '';
          }
          if (dataLines.length > 0) {
            const data = dataLines.join('\n');
            dataLines = [];

            try {
              const parsed = JSON.parse(data) as Record<string, unknown>;

              if (currentEvent === 'text') {
                const text = typeof parsed.text === 'string' ? parsed.text : '';
                if (text) yield { kind: 'text', content: text };
              } else if (currentEvent === 'tool_call_start') {
                const id = typeof parsed.id === 'string' ? parsed.id : '';
                const name = typeof parsed.name === 'string' ? parsed.name : 'tool';
                const startedAt = Date.now();
                toolBuffers.set(id, { name, input: '', startedAt });
                yield {
                  kind: 'tool_call',
                  toolId: id,
                  name,
                  input: '',
                  startedAt,
                };
              } else if (currentEvent === 'tool_call_input') {
                const id = typeof parsed.id === 'string' ? parsed.id : '';
                const partialJson = typeof parsed.partialJson === 'string' ? parsed.partialJson : '';
                const tc = toolBuffers.get(id);
                if (tc) {
                  tc.input += partialJson;
                  // Deliver the accumulated args to the pending segment so the tool
                  // panel shows the command / path / diff while it runs. Previously
                  // this was buffered and then dropped — leaving the UI with just
                  // the tool name.
                  yield { kind: 'tool_call_input', toolId: id, input: tc.input };
                }
              } else if (currentEvent === 'tool_result') {
                const id = typeof parsed.id === 'string' ? parsed.id : '';
                const result = typeof parsed.result === 'string' ? parsed.result : '';
                const isError = parsed.isError === true;
                const isWarning = parsed.isWarning === true;
                const tc = toolBuffers.get(id);
                const elapsed = tc ? Date.now() - tc.startedAt : 0;
                if (tc) toolBuffers.delete(id);
                yield {
                  kind: 'tool_result',
                  toolId: id,
                  output: result,
                  durationMs: elapsed,
                  isError,
                  ...(isWarning ? { isWarning } : {}),
                };
              } else if (currentEvent === 'turn_complete') {
                // Consolidated end-of-turn event — usage, cost, and context in one payload.
                const u = (parsed.usage ?? {}) as Record<string, number>;
                const c = (parsed.cost ?? {}) as Record<string, unknown>;
                yield {
                  kind: 'turn_complete' as const,
                  usage: {
                    inputTokens: u.inputTokens ?? 0,
                    outputTokens: u.outputTokens ?? 0,
                    cacheReadTokens: u.cacheReadTokens ?? 0,
                    cacheWriteTokens: u.cacheWriteTokens ?? 0,
                  },
                  cost: {
                    inputCost: (c.inputCost as number) ?? 0,
                    outputCost: (c.outputCost as number) ?? 0,
                    cacheReadCost: (c.cacheReadCost as number) ?? 0,
                    cacheWriteCost: (c.cacheWriteCost as number) ?? 0,
                    totalCost: (c.totalCost as number) ?? 0,
                    model: (c.model as string) ?? '',
                  },
                  durationMs: (parsed.durationMs as number) ?? 0,
                  ...(typeof parsed.lastPromptTokens === 'number' ? { lastPromptTokens: parsed.lastPromptTokens } : {}),
                  messageCount: (parsed.messageCount as number) ?? 0,
                };
              } else if (currentEvent === 'done') {
                state.terminal = true;
                yield { kind: 'done' };
                return;
              } else if (currentEvent === 'error') {
                const message =
                  typeof parsed.error === 'string'
                    ? parsed.error
                    : typeof parsed.message === 'string'
                      ? parsed.message
                      : 'Unknown error';
                state.terminal = true;
                yield { kind: 'error', message };
                // An error is terminal for the turn — stop reading so a stray
                // post-error text event can't mutate a finished turn (mirrors the
                // `done` branch; the server now always sends `done` after `error`).
                return;
              } else if (currentEvent === 'route') {
                // Backend routing truth — the UI reflects what actually runs,
                // not what it last guessed (single source of truth). Capture the
                // sessionId so a brand-new conversation can still reconnect.
                if (typeof parsed.sessionId === 'string' && parsed.sessionId) state.sessionId = parsed.sessionId;
                yield {
                  kind: 'route',
                  provider: typeof parsed.provider === 'string' ? parsed.provider : '',
                  model: typeof parsed.model === 'string' ? parsed.model : '',
                  useExtendedContext: parsed.useExtendedContext === true,
                  reason: typeof parsed.reason === 'string' ? parsed.reason : '',
                  effort: typeof parsed.effort === 'string' ? parsed.effort : '',
                };
              } else if (currentEvent === 'system') {
                const content = typeof parsed.content === 'string' ? parsed.content : '';
                if (content) yield { kind: 'system', content };
              } else if (currentEvent === 'turn_meta') {
                // Turn injection metadata — structured pass-through from the server.
                const meta = parsed as Record<string, unknown>;
                yield {
                  kind: 'turn_meta' as const,
                  executor: ((meta.executor as string) === 'cli' ? 'cli' : 'api') as 'cli' | 'api',
                  systemPrompt: (meta.systemPrompt ?? {
                    sections: [],
                    totalChars: 0,
                    totalTokens: 0,
                  }) as Extract<StreamEvent, { kind: 'turn_meta' }>['systemPrompt'],
                  budget: (meta.budget ?? {
                    modelWindow: 0,
                    maxOutput: 0,
                    systemReserved: 0,
                    historyBudget: 0,
                  }) as Extract<StreamEvent, { kind: 'turn_meta' }>['budget'],
                  context: (meta.context ?? {
                    messageCount: 0,
                    hasSummary: false,
                    nearlyFull: false,
                  }) as Extract<StreamEvent, { kind: 'turn_meta' }>['context'],
                };
              } else if (currentEvent === 'question') {
                // Mid-turn operator question (ask_operator). The server spec's
                // `kind` field is renamed `questionKind` client-side so it
                // can't collide with the StreamEvent discriminant.
                const rawOpts = Array.isArray(parsed.options) ? (parsed.options as Array<Record<string, unknown>>) : [];
                yield {
                  kind: 'question',
                  id: typeof parsed.id === 'string' ? parsed.id : '',
                  question: typeof parsed.question === 'string' ? parsed.question : '',
                  ...(typeof parsed.header === 'string' ? { header: parsed.header } : {}),
                  questionKind: (['yes_no', 'choice', 'multi', 'text'].includes(parsed.kind as string)
                    ? parsed.kind
                    : 'text') as 'yes_no' | 'choice' | 'multi' | 'text',
                  options: rawOpts
                    .filter((o) => typeof o.label === 'string')
                    .map((o) => ({
                      label: o.label as string,
                      ...(typeof o.description === 'string' ? { description: o.description } : {}),
                    })),
                  timeoutMs: typeof parsed.timeoutMs === 'number' ? parsed.timeoutMs : 0,
                  askedAt: typeof parsed.askedAt === 'string' ? parsed.askedAt : '',
                };
              } else if (currentEvent === 'question_resolved') {
                yield {
                  kind: 'question_resolved',
                  id: typeof parsed.id === 'string' ? parsed.id : '',
                };
              } else if (currentEvent === 'subagent') {
                const subType = parsed.type as string;
                if (
                  subType === 'tool_start' ||
                  subType === 'tool_input' ||
                  subType === 'tool_result' ||
                  subType === 'text'
                ) {
                  yield {
                    kind: 'subagent',
                    subType,
                    ...(typeof parsed.name === 'string' ? { name: parsed.name } : {}),
                    ...(typeof parsed.id === 'string' ? { id: parsed.id } : {}),
                    ...(typeof parsed.partialJson === 'string' ? { partialJson: parsed.partialJson } : {}),
                    ...(typeof parsed.result === 'string' ? { result: parsed.result } : {}),
                    ...(typeof parsed.isError === 'boolean' ? { isError: parsed.isError } : {}),
                    ...(typeof parsed.text === 'string' ? { text: parsed.text } : {}),
                  };
                }
              } else if (currentEvent === 'turn') {
                // Attach handshake — identifies which run this viewer joined.
                const runId = typeof parsed.runId === 'string' ? parsed.runId : null;
                if (state.runId !== null && runId !== null && runId !== state.runId) {
                  // Re-attached to a DIFFERENT (superseding) turn: our seq
                  // state is stale against it, and its owner has its own
                  // stream. End this one cleanly rather than mixing chunks.
                  state.terminal = true;
                  yield { kind: 'done' };
                  return;
                }
                if (runId !== null) state.runId = runId;
              }
            } catch {
              // Non-JSON data
              if (currentEvent === 'text') {
                yield { kind: 'text', content: data };
              }
            }

            currentEvent = '';
          }
        } else if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('id: ')) {
          currentId = line.slice(4).trim();
        } else if (line.startsWith('data: ')) {
          dataLines.push(line.slice(6));
        }
      }
    }
  } finally {
    // Always release the reader lock. On a clean break the stream is already
    // closed; on an idle/abort/throw return the socket may still be open.
    void reader.cancel().catch(() => undefined);
  }
  // Body ended. If no terminal event arrived, state.terminal stays false and the
  // caller (streamChat) decides whether to reconnect.
}
